import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Mail, Lock, User, ArrowLeft, CalendarDays, Gift, Phone } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth, LanguageToggle, API } from '../App';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const RegisterPage = () => {
  const { t, i18n } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [birthdayDay, setBirthdayDay] = useState('');
  const [birthdayMonth, setBirthdayMonth] = useState('');
  const [birthdayYear, setBirthdayYear] = useState('');
  const [namedayDay, setNamedayDay] = useState('');
  const [namedayMonth, setNamedayMonth] = useState('');
  const [namedayConfirmed, setNamedayConfirmed] = useState(false);
  const [namedayOptions, setNamedayOptions] = useState([]);
  const [selectedNamedayId, setSelectedNamedayId] = useState('');
  const [namedaySearching, setNamedaySearching] = useState(false);
  const [manualNameday, setManualNameday] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bgImage, setBgImage] = useState('https://images.unsplash.com/photo-1726906741385-c114d7fdd845?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxoYWlyJTIwZmFzaGlvbiUyMHJ1bndheSUyMGRhcmt8ZW58MHx8fHwxNzcyMDg1OTA0fDA&ixlib=rb-4.1.0&q=85');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        if (res.data.login_bg_image_url) {
          setBgImage(res.data.login_bg_image_url);
        }
      } catch (e) {
        console.error('Failed to fetch settings', e);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    const lookupName = name.trim();
    setSelectedNamedayId('');
    setNamedayOptions([]);
    setManualNameday(false);
    setNamedayConfirmed(false);
    if (lookupName.length < 2) return undefined;

    const timer = setTimeout(async () => {
      setNamedaySearching(true);
      try {
        const res = await axios.get(`${API}/namedays/options?name=${encodeURIComponent(lookupName)}`);
        const options = res.data?.options || [];
        setNamedayOptions(options);
        if (options.length === 1) {
          setSelectedNamedayId(String(options[0].id));
          setNamedayDay(String(options[0].day));
          setNamedayMonth(String(options[0].month));
          setNamedayConfirmed(true);
        }
      } catch (e) {
        setNamedayOptions([]);
      } finally {
        setNamedaySearching(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [name]);

  const selectNamedayOption = (option) => {
    setSelectedNamedayId(String(option.id));
    setNamedayDay(String(option.day));
    setNamedayMonth(String(option.month));
    setNamedayConfirmed(true);
    setManualNameday(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone.trim()) {
      toast.error('Το κινητό τηλέφωνο είναι υποχρεωτικό');
      return;
    }
    setLoading(true);
    try {
      const selectedNameday = namedayOptions.find((option) => String(option.id) === String(selectedNamedayId));
      await register(email, password, name, i18n.language, {
        phone,
        birthday_day: birthdayDay ? Number(birthdayDay) : null,
        birthday_month: birthdayMonth ? Number(birthdayMonth) : null,
        birthday_year: birthdayYear ? Number(birthdayYear) : null,
        nameday_day: selectedNameday ? selectedNameday.day : (namedayDay ? Number(namedayDay) : null),
        nameday_month: selectedNameday ? selectedNameday.month : (namedayMonth ? Number(namedayMonth) : null),
        nameday_date_rule: selectedNameday || null,
        nameday_name: name.split(' ')[0],
        nameday_confirmed: Boolean(selectedNameday || namedayConfirmed),
        nameday_source: selectedNameday ? 'user_selected' : (namedayConfirmed ? 'user_selected_manual' : 'unknown'),
        nameday_options: selectedNameday ? namedayOptions : [],
        profile_completion_needed: !selectedNameday && !namedayConfirmed
      });
      toast.success(t('common.success'));
      navigate('/profile');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-misel-bg flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: `url('${bgImage}')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-misel-bg via-misel-bg/50 to-transparent" />
        <div className="relative z-10 p-12 flex flex-col justify-between h-full">
          <Link to="/" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
            <ArrowLeft size={20} />
            <span className="font-roboto font-bold tracking-wider">MISEL GROUP DIGITAL LAB</span>
          </Link>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile back button */}
          <Link 
            to="/" 
            className="lg:hidden flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft size={20} />
            <span className="font-roboto font-bold tracking-wider">MISEL GROUP DIGITAL LAB</span>
          </Link>

          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="font-roboto font-bold text-3xl text-white tracking-wide mb-2">
                {t('auth.register_title')}
              </h1>
              <p className="text-gray-400">
                {t('auth.register_subtitle')}
              </p>
            </div>
            <LanguageToggle />
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm text-gray-400">{t('auth.name')}</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-primary"
                  placeholder="John Doe"
                  required
                  data-testid="register-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-gray-400">{t('auth.email')}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-primary"
                  placeholder="email@example.com"
                  required
                  data-testid="register-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-gray-400">Κινητό τηλέφωνο</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-primary"
                  placeholder="π.χ. 6947300987"
                  required
                  data-testid="register-phone"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-gray-400">{t('auth.password')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-primary"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="register-password"
                />
              </div>
            </div>

            <div className="space-y-3 rounded-sm border border-white/10 bg-white/5 p-4">
              <div className="flex items-center gap-2 text-white">
                <CalendarDays size={18} className="text-primary" />
                <span className="font-medium">
                  {i18n.language === 'el'
                    ? 'Πότε έχεις γενέθλια; Θέλουμε να είμαστε μαζί σου στις σημαντικές σου στιγμές.'
                    : "When is your birthday? We'd like to be with you on your important moments."}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Input type="number" min="1" max="31" value={birthdayDay} onChange={(e) => setBirthdayDay(e.target.value)} className="bg-white/5 border-white/10 text-white" placeholder={i18n.language === 'el' ? 'Ημέρα' : 'Day'} />
                <Input type="number" min="1" max="12" value={birthdayMonth} onChange={(e) => setBirthdayMonth(e.target.value)} className="bg-white/5 border-white/10 text-white" placeholder={i18n.language === 'el' ? 'Μήνας' : 'Month'} />
                <Input type="number" min="1900" max="2100" value={birthdayYear} onChange={(e) => setBirthdayYear(e.target.value)} className="bg-white/5 border-white/10 text-white" placeholder={i18n.language === 'el' ? 'Έτος προαιρετικό' : 'Year optional'} />
              </div>
            </div>

            <div className="space-y-3 rounded-sm border border-white/10 bg-white/5 p-4">
              <div className="flex items-center gap-2 text-white">
                <Gift size={18} className="text-primary" />
                <span className="font-medium">
                  Θέλουμε να είμαστε μαζί σου στη σημαντική ημέρα της ονομαστικής σου εορτής. Επιβεβαίωσε μας την ακριβή ημερομηνία.
                </span>
              </div>
              {namedaySearching && <p className="text-sm text-gray-400">Αναζήτηση ονομαστικής εορτής...</p>}
              {!namedaySearching && namedayOptions.length > 1 && (
                <p className="text-sm text-gray-300">Για το όνομά σου βρήκαμε περισσότερες από μία πιθανές ημερομηνίες. Ποια είναι η σωστή;</p>
              )}
              {!namedaySearching && name.trim().length >= 2 && namedayOptions.length === 0 && (
                <p className="text-sm text-gray-400">Δεν βρήκαμε αυτόματα την ονομαστική εορτή. Μπορείς να την προσθέσεις χειροκίνητα.</p>
              )}
              {namedayOptions.length > 0 && (
                <div className="space-y-2">
                  {namedayOptions.map((option) => (
                    <label key={`${option.id}-${option.date}`} className="flex items-center gap-3 rounded-sm border border-white/10 bg-black/20 p-3 text-sm text-gray-200">
                      <input type="radio" name="nameday-option" checked={String(selectedNamedayId) === String(option.id)} onChange={() => selectNamedayOption(option)} />
                      <span>{new Date(option.date).toLocaleDateString('el-GR', { day: 'numeric', month: 'long' })} - {option.label_el || option.canonical_name}</span>
                    </label>
                  ))}
                  <button type="button" className="text-sm text-primary hover:underline" onClick={() => { setManualNameday(true); setSelectedNamedayId(''); setNamedayConfirmed(false); }}>
                    Δεν είναι καμία από αυτές
                  </button>
                </div>
              )}
              {(manualNameday || (name.trim().length >= 2 && !namedaySearching && namedayOptions.length === 0)) && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <Input type="number" min="1" max="31" value={namedayDay} onChange={(e) => setNamedayDay(e.target.value)} className="bg-white/5 border-white/10 text-white" placeholder="Ημέρα" />
                    <Input type="number" min="1" max="12" value={namedayMonth} onChange={(e) => setNamedayMonth(e.target.value)} className="bg-white/5 border-white/10 text-white" placeholder="Μήνας" />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input type="checkbox" checked={namedayConfirmed} onChange={(e) => setNamedayConfirmed(e.target.checked)} />
                    Η ημερομηνία της ονομαστικής εορτής είναι σωστή
                  </label>
                </>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit"
              className="w-full bg-primary hover:bg-primary/90 text-white font-semibold tracking-wide uppercase py-6"
            >
              {loading ? t('common.loading') : t('auth.register_btn')}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-gray-400">
              {t('auth.has_account')}{' '}
              <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
                {t('auth.login_btn')}
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RegisterPage;
